import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToast } from '@/hooks/use-toast';
import { scenarioAssignmentsApi, scenarioRunsApi, scenariosApi } from '@/lib/api/scenarios.api';
import { extractErrorMessage } from '@/lib/utils';

import type {
  CreateAssignmentInput,
  CreateScenarioInput,
  ScenarioListParams,
  UpdateAssignmentInput,
  UpdateScenarioInput,
} from '@/lib/api/scenarios.api';

export const SCENARIOS_QK = ['scenarios'];
export const SCENARIO_ASSIGNMENTS_QK = ['scenario-assignments'];
export const SCENARIO_RUNS_QK = ['scenario-runs'];

export function useScenarioList(params?: ScenarioListParams) {
  return useQuery({
    queryKey: [...SCENARIOS_QK, 'list', params ?? {}],
    queryFn: () => scenariosApi.list(params),
  });
}

export function useScenario(id: string | undefined) {
  return useQuery({
    queryKey: [...SCENARIOS_QK, 'one', id],
    queryFn: () => scenariosApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateScenario() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (input: CreateScenarioInput) => scenariosApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SCENARIOS_QK });
      toast({ title: 'Scenario created' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Could not create scenario',
        description: extractErrorMessage(error),
      });
    },
  });
}

export function useUpdateScenario() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateScenarioInput }) =>
      scenariosApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SCENARIOS_QK });
      toast({ title: 'Scenario saved' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Could not save scenario',
        description: extractErrorMessage(error),
      });
    },
  });
}

export function useDeleteScenario() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => scenariosApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SCENARIOS_QK });
      qc.invalidateQueries({ queryKey: SCENARIO_ASSIGNMENTS_QK });
      toast({ title: 'Scenario deleted' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Could not delete scenario',
        description: extractErrorMessage(error),
      });
    },
  });
}

export function useDuplicateScenario() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => scenariosApi.duplicate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SCENARIOS_QK });
      toast({ title: 'Scenario duplicated' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Could not duplicate scenario',
        description: extractErrorMessage(error),
      });
    },
  });
}

export function useDraftScenario() {
  const { toast } = useToast();
  return useMutation({
    mutationFn: (description: string) => scenariosApi.draft(description),
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Could not draft scenario',
        description: extractErrorMessage(error),
      });
    },
  });
}

export function useScenarioAssignments(params?: {
  configuration_id?: string;
  scenario_id?: string;
}) {
  const enabled = !!(params?.configuration_id || params?.scenario_id);
  return useQuery({
    queryKey: [...SCENARIO_ASSIGNMENTS_QK, params ?? {}],
    queryFn: () => scenarioAssignmentsApi.list(params),
    enabled,
  });
}

const invalidateAssignmentKeys = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: SCENARIO_ASSIGNMENTS_QK });
  qc.invalidateQueries({ queryKey: SCENARIOS_QK });
  qc.invalidateQueries({ queryKey: ['configurations'] });
};

export function useCreateAssignment() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (input: CreateAssignmentInput) => scenarioAssignmentsApi.create(input),
    onSuccess: () => {
      invalidateAssignmentKeys(qc);
      toast({ title: 'Scenario assigned' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Could not assign scenario',
        description: extractErrorMessage(error),
      });
    },
  });
}

export function useUpdateAssignment() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAssignmentInput }) =>
      scenarioAssignmentsApi.update(id, input),
    onSuccess: () => invalidateAssignmentKeys(qc),
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Could not update assignment',
        description: extractErrorMessage(error),
      });
    },
  });
}

export function useDeleteAssignment() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => scenarioAssignmentsApi.delete(id),
    onSuccess: () => {
      invalidateAssignmentKeys(qc);
      toast({ title: 'Assignment removed' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Could not remove assignment',
        description: extractErrorMessage(error),
      });
    },
  });
}

/** Suite runs that include a scenario, for the run-history card on the scenario page. */
export function useScenarioRunsForScenario(scenarioId: string | undefined) {
  return useQuery({
    queryKey: [...SCENARIO_RUNS_QK, 'by-scenario', scenarioId],
    queryFn: () => scenarioRunsApi.list({ scenario_id: scenarioId }),
    enabled: !!scenarioId,
  });
}
